import { Component } from "@theme/component";
import { debounce } from "@theme/utilities";

const ANIMATION_OPTIONS = { duration: 500 };

function animateValue({ from, to, duration, onUpdate, easing = x => x*x*(3 - 2*x), onComplete }) {
  let start = performance.now(), done = false, current = from;
  function step(t) {
    if (done) return;
    const pct = Math.min((t - start) / duration, 1);
    const v = from + (to - from) * easing(pct);
    current = v; onUpdate(v);
    if (pct < 1) requestAnimationFrame(step); else if (typeof onComplete === "function") onComplete();
  }
  requestAnimationFrame(step);
  return { get current(){ return current; }, cancel(){ done = true; } };
}

class MarqueeComponent extends Component {
  requiredRefs = ["wrapper", "content"];
  #ready = false;
  #animEaseBack = null;
  #lastVW = window.innerWidth;
  #lastVH = window.innerHeight;

  connectedCallback() {
    super.connectedCallback();
    const { content } = this.refs;
    if (content.firstElementChild?.children.length === 0) return;

    this.#ensureCopies();
    this.#duplicateForLoop();
    this.#applySpeed();

    if (!this.#ready) {
      requestAnimationFrame(() => { this.classList.add("ready"); this.#ready = true; });
    }

    window.addEventListener("resize", this.#onResize);
    this.addEventListener("pointerleave", this.#onPointerLeave);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("resize", this.#onResize);
    this.removeEventListener("pointerleave", this.#onPointerLeave);
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const last = wrapper.lastElementChild;
    return last !== content ? last : null;
  }

  // ---- internals ----
  #speedFromAttrs() {
    const factor = Number(this.getAttribute("data-speed-factor"));
    const w = this.offsetWidth;
    return Math.ceil(w / factor / 2); // seconds
  }

  #applySpeed(seconds = this.#speedFromAttrs()) {
    this.style.setProperty("--marquee-speed", `${seconds}s`);
  }

  #visibleCopiesNeeded() {
    const { content } = this.refs;
    const hostW = this.offsetWidth;
    const unitW = content.firstElementChild instanceof HTMLElement ? content.firstElementChild.offsetWidth : 1;
    return unitW === 0 ? 1 : Math.ceil(hostW / unitW);
  }

  #ensureCopies(target = this.#visibleCopiesNeeded()) {
    const { content } = this.refs;
    const first = content.firstElementChild;
    if (!first) return;
    // add
    for (let i = content.children.length; i < target; i++) {
      content.appendChild(first.cloneNode(true));
    }
    // trim extras (keep at least 'target' children)
    while (content.children.length > target) {
      content.lastElementChild?.remove();
    }
  }

  #duplicateForLoop() {
    this.clonedContent?.remove();
    const clone = this.refs.content.cloneNode(true);
    clone.setAttribute("aria-hidden", "true");
    clone.removeAttribute("ref");
    this.refs.wrapper.appendChild(clone);
  }

  // keep animation smooth after pointer leaves (your easing back to 1x)
  #easePlaybackTo1 = debounce(() => {
    if (this.#animEaseBack) return;
    const anim = this.refs.wrapper.getAnimations()[0];
    if (!anim) return;
    this.#animEaseBack = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1, to: 0,
      onUpdate: v => anim.updatePlaybackRate(v),
      onComplete: () => { this.#animEaseBack = null; }
    });
  }, ANIMATION_OPTIONS.duration);

  #onPointerLeave = () => {
    this.#easePlaybackTo1.cancel?.();
    const anim = this.refs.wrapper.getAnimations()[0];
    if (!anim || anim.playbackRate === 1) return;
    const start = this.#animEaseBack?.current ?? 0;
    this.#animEaseBack?.cancel?.();
    this.#animEaseBack = animateValue({
      ...ANIMATION_OPTIONS,
      from: start, to: 1,
      onUpdate: v => anim.updatePlaybackRate(v),
      onComplete: () => { this.#animEaseBack = null; }
    });
  };

  // *** key fix: ignore height-only resizes + preserve progress on width change ***
  #onResize = debounce(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const dw = Math.abs(vw - this.#lastVW);
    const dh = Math.abs(vh - this.#lastVH);

    // Mobile URL-bar show/hide triggers height-only resizes -> ignore
    if (dw < 2 && dh >= 1) {
      this.#lastVH = vh; // update so we don't pile up diffs
      return;
    }

    // capture current progress before we mutate DOM/CSS
    const anim = this.refs.wrapper.getAnimations()[0];
    let progress = 0;
    if (anim?.effect) {
      const timing = anim.effect.getComputedTiming();
      progress = timing.progress ?? 0; // 0..1
    }

    // recompute only when width actually changed (or first pass)
    if (dw >= 2) {
      this.#ensureCopies();
      this.#duplicateForLoop();
      this.#applySpeed();
    }

    // restore progress instead of jumping to 0
    requestAnimationFrame(() => {
      const a = this.refs.wrapper.getAnimations()[0];
      if (a?.effect) {
        const dur = a.effect.getComputedTiming().duration; // ms
        if (typeof dur === "number" && isFinite(dur)) {
          a.currentTime = progress * dur;
        }
      }
    });

    this.#lastVW = vw;
    this.#lastVH = vh;
  }, 250);
}

customElements.get("marquee-component") ||
  customElements.define("marquee-component", MarqueeComponent);
